<script lang="ts">
	import type { Coordinates } from '$lib/components/map/types';
	import type { PoiData, SplittedAddress } from '$lib/components/pois/types';
	import type { TrafficInformationSummary } from '../traffic/types';
	import { page } from '$app/stores';
	import { type CoordinatesInput, ReverseGeocodeStore } from '$houdini';
	import AddressSummary from '$lib/components/address/summary/AddressSummary.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import PoiSummary from '$lib/components/pois/summary/PoiSummary.svelte';
	import { searchExtensionTouchpoint } from '$lib/services/searchExtension';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';
	import { createEventDispatcher } from 'svelte';
	import { get } from 'svelte/store';
	import { computeSearchParamsItinerary } from '../itinerary/util';
	import { isSummaryPoiData } from '../pois/utils';
	import TrafficSummary from '../traffic/TrafficSummary.svelte';

	export let poiData: PoiData | undefined = undefined;
	export let trafficEventData: TrafficInformationSummary | undefined = undefined;

	const { itineraryStore, isMapFullscreen, clickedPosition } = getMapLayoutContext();
	const searchParams = getSearchParamsStore();

	$: coordinates = $clickedPosition || poiData?.coordinates;
	$: lookupAddress(coordinates);
	$: ({ itineraryResultsStore } = $page.data);

	interface AddressResult {
		splittedAddress: SplittedAddress | null;
		formattedAddress?: string;
		countryCode?: string;
		city?: string;
		zipCode?: string;
		loading: boolean;
		error: boolean;
		notFound: boolean;
	};

	let reverseGeocodeStore: ReverseGeocodeStore | null = null;
	let addressResult: AddressResult | undefined = undefined;

	const dispatch = createEventDispatcher<{ summaryVisible: boolean; cancelPosition: { cancelAction: boolean } }>();

	function lookupAddress(lookupPos: Coordinates | undefined, retry?: boolean) {
		if (poiData && lookupPos) {
			return;
		}

		if (!lookupPos) {
			addressResult = undefined;
			$clickedPosition = undefined;
			return;
		}

		const position: CoordinatesInput = lookupPos;
		if (!reverseGeocodeStore) {
			reverseGeocodeStore = new ReverseGeocodeStore();
		}
		addressResult = {
			loading: true,
			error: !!retry,
			notFound: false,
			splittedAddress: null,
		};
		reverseGeocodeStore
			.fetch({ variables: { position }, blocking: true })
			.then((result) => {
				if (result.data?.reverseGeocode.__typename === 'ReverseGeocodeSuccessResult') {
					const splittedAddress: SplittedAddress = {
						...result.data.reverseGeocode.splittedAddress,
					};
					if (splittedAddress && !splittedAddress.firstLine?.trim()) {
						splittedAddress.firstLine = splittedAddress.secondLine;
						splittedAddress.secondLine = ' ';
					}
					addressResult = {
						splittedAddress,
						formattedAddress: result.data.reverseGeocode.address?.formattedAddress || undefined,
						countryCode: result.data.reverseGeocode.address.country?.code,
						city: result.data.reverseGeocode.address.city || undefined,
						zipCode: result.data.reverseGeocode.address.zipCode || undefined,
						loading: false,
						error: false,
						notFound: false,
					};
				} else {
					addressResult = {
						notFound: true,
						loading: false,
						error: false,
						splittedAddress: null,
					};
				}
			})
			.catch(() => {
				addressResult = {
					error: true,
					loading: false,
					notFound: false,
					splittedAddress: null,
				};
			})
			.finally(() => {
				track({
					action: 'map_marker_placed',
					payload: {
						action: 'click',
						result: addressResult?.formattedAddress,
						country: addressResult?.countryCode,
						city: addressResult?.city,
						zipcode: addressResult?.zipCode,
					},
				});
			});
	}

	async function addAddressWaypoint() {
		if ((!addressResult && !poiData) || !coordinates) {
			return;
		}

		itineraryResultsStore?.reset();

		if ($isMapFullscreen) {
			$isMapFullscreen = false;
		}

		itineraryStore.addToItinerary({
			coordinates,
			label: addressResult?.formattedAddress || poiData?.name,
		});
		const itinerary = await computeSearchParamsItinerary(get(itineraryStore.itinerary));

		track({
			action: 'map_add_step',
			payload: {
				action: 'click',
				result: addressResult?.formattedAddress || poiData?.name,
				country: addressResult?.countryCode || (poiData?.address.country?.code ?? undefined),
				city: addressResult?.city || (poiData?.address.city ?? undefined),
				zipcode: addressResult?.zipCode || (poiData?.address.zipCode ?? undefined),
			},
		});

		searchExtensionTouchpoint.activate('map_add_step_compute');
		gotoWithParams(`/routes`, { ...$searchParams, itinerary });
		dispatch('cancelPosition', { cancelAction: false });
	}

	$: canShowSummaryCard = (poiData && isSummaryPoiData(poiData)) || addressResult || trafficEventData;

	$: if (canShowSummaryCard) {
		dispatch('summaryVisible', true);
	} else {
		dispatch('summaryVisible', false);
	}
</script>

{#if canShowSummaryCard}
	<div
		data-testid="floating-summary"
	>
		{#if poiData && isSummaryPoiData(poiData)}
			<PoiSummary {poiData} on:addClick={addAddressWaypoint} />
		{:else if addressResult}
			<AddressSummary
				address={addressResult.splittedAddress}
				isLoading={addressResult.loading}
				hasError={addressResult.error}
				notFound={addressResult.notFound}
				on:retryClick={() => lookupAddress($clickedPosition)}
				on:addClick={addAddressWaypoint}
				on:cancelPosition
			/>
		{:else if trafficEventData}
			<TrafficSummary trafficEvent={trafficEventData} on:closeTrafficSummary />
		{/if}
	</div>
{/if}
